import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import posthog from 'posthog-js';

import { useAdexiumAds } from '@/common/hooks/adexium-ads';
import { entriesToSingleFieldObjArray } from '@/common/lib/array';
import { Playdeck } from '@/common/lib/playdeck';
import { type ShowAdsCallbackArgsType } from '@/common/types/ads';

import { useAdsgramAds } from './hooks/adsgram-ads';
// import { useMonetagAds } from './hooks/monetag-ads';
import { AdsContext } from './context';

const AdsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [adsIsLoading, setAdsIsLoading] = useState(false);
  const [lastAdsIndex, setLastAdsIndex] = useState(
    Math.floor(Math.random() * 5) - 1,
  );
  const runAdsgramAds = useAdsgramAds();
  // const runMonetagAds = useMonetagAds();
  const runAdexiumAds = useAdexiumAds();
  // Monetag temporarily disabled — uncomment useMonetagAds import/hook,
  // then re-add runMonetagAds to adsList and 'monetag' to adsByIndex.
  const adsList = useMemo(
    () => [runAdsgramAds, runAdexiumAds],
    [runAdsgramAds, runAdexiumAds],
  );
  const showAd = useCallback(
    async ({
      placements,
      onError,
      onReward,
      reward,
    }: ShowAdsCallbackArgsType) => {
      const adsByIndex = ['adsgram', 'adexium'];
      setAdsIsLoading(true);
      const attemptedProviders: string[] = [];
      const startedAt = performance.now();

      for (let index = 0; index < adsList.length; index++) {
        const adsIndex = (lastAdsIndex + 1 + index) % adsList.length;
        const runAds = adsList[adsIndex];
        const provider = adsByIndex[adsIndex];
        attemptedProviders.push(provider);
        console.log(`try to run ${provider}`);

        const providerStartedAt = performance.now();

        try {
          Playdeck.showAd();
          await runAds({ placements, onReward });
          setLastAdsIndex(adsIndex);
          setAdsIsLoading(false);
          posthog.capture('ad_provider_shown', {
            provider,
            placements,
            attempt_order: attemptedProviders.length,
            previous_providers_failed: attemptedProviders.slice(0, -1),
            duration_ms: Math.round(performance.now() - providerStartedAt),
          });
          Playdeck.sendAnalitics({
            type: 'custom_event',
            name: 'free_bonus',
            user_properties: {},
            event_properties: {
              amount: 1,
              type: 'adview',
              reward: reward
                ? Object.entries(reward as Record<string, number>).map(
                    entriesToSingleFieldObjArray,
                  )
                : [],
            },
          });

          return;
        } catch (error) {
          console.error(error);
          posthog.capture('ad_provider_failed', {
            provider,
            placements,
            error_message:
              error instanceof Error ? error.message : String(error),
            attempt_order: attemptedProviders.length,
            duration_ms: Math.round(performance.now() - providerStartedAt),
          });
        }
      }

      posthog.capture('ad_all_providers_failed', {
        placements,
        attempted_providers: attemptedProviders,
        duration_ms: Math.round(performance.now() - startedAt),
      });

      if (onError) {
        onError();
      }
      setAdsIsLoading(false);
    },
    [adsList, lastAdsIndex],
  );

  return (
    <AdsContext.Provider value={{ showAd, adsIsLoading }}>
      {children}
    </AdsContext.Provider>
  );
};

export { AdsProvider };
